import React from "react";
import DOMPurify from "dompurify";
import { 
  Card, CardContent, CardHeader, CardTitle, CardDescription 
} from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { CopyEventLinkButton } from "./CopyEventLinkButton";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle 
} from "./components/ui/dialog";
import { 
  Calendar, MapPin, Users, Euro, ExternalLink, Edit, Trash2, Tag,
  Clock, Building2, User, Phone, Mail, Globe, X, CheckCircle, AlertCircle
} from "lucide-react";

// Helper function to strip HTML tags from text for preview display
const stripHtmlTags = (html) => {
  if (!html) return '';
  // Create a temporary element to parse HTML and extract text content
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// True for events still using the old (standard / opera-matrix) pricing model.
// Members can no longer book these — they stay visible for admins + financial
// history but the booking CTA is replaced by a "Booking closed" badge.
const isLegacyPricing = (event) => event?.pricing_type !== 'custom_rows';

// Shared formatting function for event pricing
const formatEventPricing = (event) => {
  const { matrix_pricing: matrixPricing, price_categories: priceCategories, cost_euro_full: fullCost, cost_euro_reduced: reducedCost } = event;

  // New custom_rows pricing takes priority — 1..5 admin-defined labelled prices.
  if (event?.pricing_type === 'custom_rows' && Array.isArray(event.custom_price_rows) && event.custom_price_rows.length > 0) {
    const priced = event.custom_price_rows.filter(r => Number(r?.amount) > 0);
    if (priced.length === 0) return 'Free Event';
    return priced.map(r => `€${Number(r.amount).toFixed(2)} (${r.label})`).join(' / ');
  }
  // Handle standard sophisticated pricing structure FIRST (most common)
  if (priceCategories) {
    const enabledPrices = [];
    
    // Check for valid (non-null, non-zero) prices
    if (priceCategories.member_price_enabled && priceCategories.member_price != null && priceCategories.member_price > 0) {
      enabledPrices.push(`€${parseFloat(priceCategories.member_price).toFixed(2)} (Member)`);
    }
    if (priceCategories.minor_price_enabled && priceCategories.minor_price != null && priceCategories.minor_price > 0) {
      enabledPrices.push(`€${parseFloat(priceCategories.minor_price).toFixed(2)} (Minor)`);
    }
    if (priceCategories.senior_price_enabled && priceCategories.senior_price != null && priceCategories.senior_price > 0) {
      enabledPrices.push(`€${parseFloat(priceCategories.senior_price).toFixed(2)} (Senior)`);
    }
    if (priceCategories.young_adult_price_enabled && priceCategories.young_adult_price != null && priceCategories.young_adult_price > 0) {
      enabledPrices.push(`€${parseFloat(priceCategories.young_adult_price).toFixed(2)} (Young Adult)`);
    }
    
    
    if (enabledPrices.length > 0) {
      if (enabledPrices.length === 1) return enabledPrices[0];
      if (enabledPrices.length === 2) return enabledPrices.join(' / ');
      return `${enabledPrices[0]} / +${enabledPrices.length - 1} more`;
    }
    // Price categories exist but no valid prices - continue to matrix pricing
  }
  
  // Handle direct pricing fields (member_price, senior_price, etc.) - fallback
  const memberPrice = parseFloat(event.member_price) || 0;
  const seniorPrice = parseFloat(event.senior_price) || 0;
  const minorPrice = parseFloat(event.minor_price) || 0;
  const youngAdultPrice = parseFloat(event.young_adult_price) || 0;
  
  if (memberPrice > 0 || seniorPrice > 0 || minorPrice > 0 || youngAdultPrice > 0) {
    const enabledPrices = [];
    
    if (memberPrice > 0) {
      enabledPrices.push(`€${memberPrice.toFixed(2)} (Member)`);
    }
    if (seniorPrice > 0) {
      enabledPrices.push(`€${seniorPrice.toFixed(2)} (Senior)`);
    }
    if (minorPrice > 0) {
      enabledPrices.push(`€${minorPrice.toFixed(2)} (Minor)`);
    }
    if (youngAdultPrice > 0) {
      enabledPrices.push(`€${youngAdultPrice.toFixed(2)} (Young Adult)`);
    }
    
    if (enabledPrices.length === 0) return "Free Event";
    if (enabledPrices.length === 1) return enabledPrices[0];
    if (enabledPrices.length === 2) return enabledPrices.join(' / ');
    return `${enabledPrices[0]} / +${enabledPrices.length - 1} more`;
  }
  
  // Handle matrix pricing structure (Opera-style pricing)
  if (matrixPricing && Object.keys(matrixPricing).length > 0) {
    const sectionPrices = [];
    
    // Process Galleria pricing
    if (matrixPricing.galleria_enabled) {
      let galleriaPrice = null;
      if (matrixPricing.member_pricing_enabled && matrixPricing.member_pricing?.galleria_price > 0) {
        galleriaPrice = matrixPricing.member_pricing.galleria_price;
      } else if (matrixPricing.senior_pricing_enabled && matrixPricing.senior_pricing?.galleria_price > 0) {
        galleriaPrice = matrixPricing.senior_pricing.galleria_price;
      } else if (matrixPricing.minor_pricing_enabled && matrixPricing.minor_pricing?.galleria_price > 0) {
        galleriaPrice = matrixPricing.minor_pricing.galleria_price;
      } else if (matrixPricing.young_adult_pricing_enabled && matrixPricing.young_adult_pricing?.platea_price > 0) {
        galleriaPrice = matrixPricing.young_adult_pricing.galleria_price;
      }
      
      if (galleriaPrice) {
        const section1Label = event.section_1_label || "Galleria";
        sectionPrices.push(`€${parseFloat(galleriaPrice).toFixed(2)} (${section1Label})`);
      }
    }
    
    // Process Platea pricing
    if (matrixPricing.platea_enabled) {
      let plateaPrice = null;
      if (matrixPricing.member_pricing_enabled && matrixPricing.member_pricing?.platea_price > 0) {
        plateaPrice = matrixPricing.member_pricing.platea_price;
      } else if (matrixPricing.senior_pricing_enabled && matrixPricing.senior_pricing?.platea_price > 0) {
        plateaPrice = matrixPricing.senior_pricing.platea_price;
      } else if (matrixPricing.minor_pricing_enabled && matrixPricing.minor_pricing?.platea_price > 0) {
        plateaPrice = matrixPricing.minor_pricing.platea_price;
      } else if (matrixPricing.young_adult_pricing_enabled && matrixPricing.young_adult_pricing?.platea_price > 0) {
        plateaPrice = matrixPricing.young_adult_pricing.platea_price;
      }
      
      if (plateaPrice) {
        const section2Label = event.section_2_label || "Platea";
        sectionPrices.push(`€${parseFloat(plateaPrice).toFixed(2)} (${section2Label})`);
      }
    }
    
    if (sectionPrices.length === 0) return "Free Event";
    if (sectionPrices.length === 1) return sectionPrices[0];
    return sectionPrices.join(' / ');
  }
  
  // Fallback to old pricing structure
  const fullPrice = fullCost && fullCost > 0 ? `€${parseFloat(fullCost).toFixed(2)}` : null;
  const reducedPrice = reducedCost && reducedCost > 0 ? `€${parseFloat(reducedCost).toFixed(2)}` : null;
  
  if (!fullPrice && !reducedPrice) return "Free Event";
  if (fullPrice && reducedPrice) return `${fullPrice} / ${reducedPrice} (reduced)`;
  if (fullPrice) return fullPrice;
  if (reducedPrice) return `${reducedPrice} (reduced price)`;
  return "Free Event";
};

export const EventsTabContent = ({ 
  events, 
  organisers = [],
  venues = [],
  currentUser, 
  editEvent, 
  deleteEvent,
  viewEvent,
  onBookEvent,
  onViewEventBookings,
  bookedEventIds = [],
  pendingBookingsByEvent = {},
  eventAvailability = {},
  viewMode = "cards" 
}) => {
  // Helper function to get event image URL with S3 support
  const getEventImageUrl = (event) => {
    // Priority 1: Use S3 images via backend proxy if available
    if (event.images_s3 && event.images_s3.length > 0) {
      const filename = event.images_s3[0].split('/').pop(); // Extract filename from S3 key
      return `${process.env.REACT_APP_BACKEND_URL}/api/images/event/${filename}`;
    }
    
    // Priority 2: Use image URLs directly if available
    if (event.image_urls && event.image_urls.length > 0) {
      const imageUrl = event.image_urls[0];
      const filename = imageUrl.split('/').pop(); // Extract filename from URL
      return `${process.env.REACT_APP_BACKEND_URL}/api/images/event/${filename}`;
    }
    
    // Priority 3: Use local images
    if (event.images && event.images.length > 0) {
      const filename = event.images[0];
      return `${process.env.REACT_APP_BACKEND_URL}/api/images/event/${filename}`;
    }
    
    return null;
  };

  // Add error handling for missing event images
  const handleEventImageError = (event, eventData) => {
    // Only log if this seems like an error (event has image data but fails to load)
    const hasImageData = eventData.images?.length > 0 || eventData.images_s3?.length > 0 || eventData.image_urls?.length > 0;
    if (hasImageData) {
      console.warn(`Event image failed to load for event: ${eventData.title} (ID: ${eventData.id})`);
    }
    
    // Hide the broken image and show placeholder
    event.target.style.display = 'none';
    if (event.target.nextSibling) {
      event.target.nextSibling.style.display = 'flex';
    }
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "Free";
    return `€${parseFloat(amount).toFixed(2)}`;
  };
  const formatEventCosts = (fullCost, reducedCost, priceCategories, matrixPricing = null) => {
    // Handle matrix pricing first (Opera-style Galleria/Platea)
    if (matrixPricing) {
      const sectionPrices = [];
      
      // Check for Galleria pricing (use the first available price from any enabled category)
      if (matrixPricing.galleria_enabled) {
        let galleriaPrice = null;
        if (matrixPricing.member_pricing_enabled && matrixPricing.member_pricing?.galleria_price > 0) {
          galleriaPrice = matrixPricing.member_pricing.galleria_price;
        } else if (matrixPricing.senior_pricing_enabled && matrixPricing.senior_pricing?.galleria_price > 0) {
          galleriaPrice = matrixPricing.senior_pricing.galleria_price;
        } else if (matrixPricing.minor_pricing_enabled && matrixPricing.minor_pricing?.galleria_price > 0) {
          galleriaPrice = matrixPricing.minor_pricing.galleria_price;
        } else if (matrixPricing.young_adult_pricing_enabled && matrixPricing.young_adult_pricing?.galleria_price > 0) {
          galleriaPrice = matrixPricing.young_adult_pricing.galleria_price;
        }
        
        if (galleriaPrice) {
          const section1Label = event.section_1_label || "Galleria";
          sectionPrices.push(`€${parseFloat(galleriaPrice).toFixed(2)} (${section1Label})`);
        }
      }
      
      // Check for Platea pricing (use the first available price from any enabled category)
      if (matrixPricing.platea_enabled) {
        let plateaPrice = null;
        if (matrixPricing.member_pricing_enabled && matrixPricing.member_pricing?.platea_price > 0) {
          plateaPrice = matrixPricing.member_pricing.platea_price;
        } else if (matrixPricing.senior_pricing_enabled && matrixPricing.senior_pricing?.platea_price > 0) {
          plateaPrice = matrixPricing.senior_pricing.platea_price;
        } else if (matrixPricing.minor_pricing_enabled && matrixPricing.minor_pricing?.platea_price > 0) {
          plateaPrice = matrixPricing.minor_pricing.platea_price;
        }
        
        if (plateaPrice) {
          const section2Label = event.section_2_label || "Platea";
          sectionPrices.push(`€${parseFloat(plateaPrice).toFixed(2)} (${section2Label})`);
        }
      }
      
      if (sectionPrices.length === 0) return "Free Event";
      if (sectionPrices.length === 1) return sectionPrices[0];
      return sectionPrices.join(' / ');
    }
    
    // Handle standard sophisticated pricing structure
    if (priceCategories) {
      const enabledPrices = [];
      
      if (priceCategories.member_price_enabled && priceCategories.member_price > 0) {
        enabledPrices.push(`€${parseFloat(priceCategories.member_price).toFixed(2)} (Member)`);
      }
      if (priceCategories.minor_price_enabled && priceCategories.minor_price > 0) {
        enabledPrices.push(`€${parseFloat(priceCategories.minor_price).toFixed(2)} (Minor)`);
      }
      if (priceCategories.senior_price_enabled && priceCategories.senior_price > 0) {
        enabledPrices.push(`€${parseFloat(priceCategories.senior_price).toFixed(2)} (Senior)`);
      }
      if (priceCategories.young_adult_price_enabled && priceCategories.young_adult_price > 0) {
        enabledPrices.push(`€${parseFloat(priceCategories.young_adult_price).toFixed(2)} (Young Adult)`);
      }
      
      if (enabledPrices.length === 0) return "Free Event";
      if (enabledPrices.length === 1) return enabledPrices[0];
      if (enabledPrices.length === 2) return enabledPrices.join(' / ');
      return `${enabledPrices[0]} / +${enabledPrices.length - 1} more`;
    }
    
    // Fallback to old pricing structure
    const fullPrice = fullCost && fullCost > 0 ? formatCurrency(fullCost) : null;
    const reducedPrice = reducedCost && reducedCost > 0 ? formatCurrency(reducedCost) : null;
    
    if (!fullPrice && !reducedPrice) return "Free Event";
    if (fullPrice && reducedPrice) return `${fullPrice} / ${reducedPrice} (reduced)`;
    if (fullPrice) return fullPrice;
    if (reducedPrice) return `${reducedPrice} (reduced price)`;
    return "Free Event";
  };

  const getEventCardColor = (event) => {
    // Generate colors based on event categories or properties
    if (!event.category_tags || event.category_tags.length === 0) {
      return "bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200";
    }

    // Define color schemes for different categories
    const colorSchemes = {
      'music': 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200',
      'workshop': 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200',
      'culture': 'bg-gradient-to-br from-green-50 to-green-100 border-green-200',
      'cultural': 'bg-gradient-to-br from-green-50 to-green-100 border-green-200', 
      'art': 'bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200',
      'exhibition': 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200',
      'theater': 'bg-gradient-to-br from-red-50 to-red-100 border-red-200',
      'theatre': 'bg-gradient-to-br from-red-50 to-red-100 border-red-200',
      'dance': 'bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200',
      'lecture': 'bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200',
      'conference': 'bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200',
      'film': 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200',
      'cinema': 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200',
      'festival': 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200',
      'food': 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200',
      'sport': 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200',
      'sports': 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200',
      'education': 'bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200',
      'seminar': 'bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200',
      'networking': 'bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200',
      'social': 'bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200'
    };

    // Check if any category matches our color schemes
    const primaryCategory = event.category_tags[0]?.toLowerCase();
    const matchingCategory = Object.keys(colorSchemes).find(category => 
      primaryCategory?.includes(category) || 
      event.category_tags.some(tag => tag.toLowerCase().includes(category))
    );

    if (matchingCategory) {
      return colorSchemes[matchingCategory];
    }

    // Fallback: Generate color based on first category hash
    const colors = [
      'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200',
      'bg-gradient-to-br from-green-50 to-green-100 border-green-200',
      'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200',
      'bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200',
      'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200',
      'bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200',
      'bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200',
      'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
    ];

    // Simple hash function to consistently assign colors
    let hash = 0;
    for (let i = 0; i < primaryCategory.length; i++) {
      hash = ((hash << 5) - hash) + primaryCategory.charCodeAt(i);
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  const getOrganiserNames = (organiserIds) => {
    if (!organiserIds || organiserIds.length === 0) return [];
    return organiserIds.map(id => {
      const organiser = (Array.isArray(organisers) ? organisers : []).find(o => o.id === id);
      return organiser ? organiser.name : `Unknown (${id})`;
    });
  };

  const getVenueNamesWithDetails = (venueIds) => {
    if (!venueIds || venueIds.length === 0) return [];
    return venueIds.map(id => {
      const venue = (Array.isArray(venues) ? venues : []).find(v => v.id === id);
      return venue ? {
        name: venue.name,
        city: venue.city,
        address: venue.address,
        capacity: venue.capacity
      } : { name: `Unknown (${id})`, city: null, address: null, capacity: null };
    });
  };

  const getStatusBadge = (event) => {
    const statusColors = {
      draft: "bg-gray-100 text-gray-800",
      published: "bg-green-100 text-green-800", 
      cancelled: "bg-red-100 text-red-800"
    };

    return (
      <Badge className={statusColors[event.status] || statusColors.draft}>
        {event.status?.charAt(0).toUpperCase() + event.status?.slice(1) || 'Draft'}
      </Badge>
    );
  };

  const isEventUpcoming = (event) => {
    if (!event.event_date) return true; // If no date, assume it's bookable
    const eventDate = new Date(event.event_date);
    return eventDate > new Date();
  };

  // Check if booking is available for an event
  const isBookingAvailable = (event) => {
    if (!isEventUpcoming(event)) return false;
    
    // Check if event has any pricing configured (new sophisticated pricing)
    if (event.price_categories) {
      const hasAnyPrice = (event.price_categories.member_price_enabled && event.price_categories.member_price > 0) ||
                         (event.price_categories.minor_price_enabled && event.price_categories.minor_price > 0) ||
                         (event.price_categories.senior_price_enabled && event.price_categories.senior_price > 0) ||
                         (event.price_categories.young_adult_price_enabled && event.price_categories.young_adult_price > 0);
      return hasAnyPrice;
    }
    
    // Fallback to old pricing structure for backward compatibility
    return (event.cost_euro_full > 0 || event.cost_euro_reduced > 0);
  };

  if (viewMode === "table") {
    const showStatusColumn = currentUser?.role === 'Administrator';
    const emptyColSpan = showStatusColumn ? 6 : 5;
    return (
      <div className="rounded-lg border overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b bg-slate-50">
            <tr>
              <th className="p-2 sm:p-3 text-left w-16 sm:w-20">Image</th>
              <th className="p-2 sm:p-3 text-left">Event</th>
              <th className="p-2 sm:p-3 text-left whitespace-nowrap">Date & Time</th>
              {showStatusColumn && <th className="p-2 sm:p-3 text-left">Status</th>}
              <th className="p-2 sm:p-3 text-left">Cost</th>
              <th className="p-2 sm:p-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(events) ? events : []).map((event) => (
              <tr key={event.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => viewEvent && viewEvent(event)}>
                <td className="p-3 w-20">
                  <div className="relative w-16 h-12">
                    {getEventImageUrl(event) ? (
                      <img
                        src={getEventImageUrl(event)}
                        alt={`${event.title} image`}
                        className="w-16 h-12 object-cover rounded border border-slate-200 shadow-sm"
                        onError={(e) => handleEventImageError(e, event)}
                      />
                    ) : null}
                    <div
                      className={`w-16 h-12 ${getEventImageUrl(event) ? 'hidden' : 'flex'} items-center justify-center bg-slate-100 rounded border border-slate-200 text-slate-400`}
                    >
                      <Calendar className="h-4 w-4" />
                    </div>
                    {event.status === 'cancelled' && (
                      <div
                        className="absolute inset-0 bg-red-900/70 flex items-center justify-center rounded"
                        data-testid={`event-cancelled-overlay-row-${event.id}`}
                      >
                        <span className="text-[9px] font-bold text-white tracking-widest uppercase rotate-[-8deg]">
                          Cancelled
                        </span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <div>
                    <h3 className="font-medium hover:text-blue-600">{event.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {stripHtmlTags(event.description_text)}
                    </p>
                    {event.event_label && (
                      <Badge variant="outline" className="mt-1">
                        <Tag className="h-3 w-3 mr-1" />
                        {event.event_label}
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span className="text-sm">{formatDate(event.event_date)}</span>
                  </div>
                  {isEventUpcoming(event) && (
                    <Badge variant="outline" className="text-xs mt-1 text-blue-600">
                      Upcoming
                    </Badge>
                  )}
                </td>
                {showStatusColumn && (
                  <td className="p-3">
                    {getStatusBadge(event)}
                  </td>
                )}
                <td className="p-3">
                  <span className="font-medium text-green-600">
                    {formatEventPricing(event)}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    {/* Low ticket warning — admin only */}
                    {currentUser?.role === 'Administrator' &&
                     eventAvailability[event.id]?.remaining_tickets != null && 
                     eventAvailability[event.id].remaining_tickets > 0 && 
                     eventAvailability[event.id].remaining_tickets <= 5 && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs mr-1" data-testid={`low-ticket-table-${event.id}`}>
                        {eventAvailability[event.id].remaining_tickets} left
                      </Badge>
                    )}
                    {/* User Booking Button */}
                    {currentUser && currentUser.role !== 'Administrator' && isEventUpcoming(event) && onBookEvent && (
                      isLegacyPricing(event) ? (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-300 px-3 py-1" title="Legacy pricing — booking closed" data-testid={`legacy-locked-table-${event.id}`}>
                          <AlertCircle className="h-3 w-3 mr-1" /> Booking closed
                        </Badge>
                      ) : pendingBookingsByEvent[event.id] ? (
                        <Button
                          size="sm"
                          variant="default"
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          title="You have a pending checkout. Click to complete payment."
                          onClick={() => { window.location.href = pendingBookingsByEvent[event.id].checkout_url; }}
                          data-testid={`resume-payment-table-${event.id}`}
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          Resume payment
                        </Button>
                      ) : bookedEventIds.includes(event.id) ? (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1" data-testid={`booked-badge-${event.id}`}>
                          <CheckCircle className="h-3 w-3 mr-1" /> Booked
                        </Badge>
                      ) : eventAvailability[event.id]?.is_sold_out ? (
                        <Button 
                          size="sm" 
                          variant="default" 
                          className="bg-orange-600 hover:bg-orange-700 text-white"
                          title="Join Waiting List"
                          onClick={() => onBookEvent(event)}
                          data-testid={`join-waitlist-table-${event.id}`}
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          Waiting List
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="default" 
                          className="bg-green-600 hover:bg-green-700 text-white"
                          title="Book this event"
                          onClick={() => onBookEvent(event)}
                          data-testid={`book-event-${event.id}`}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Book
                        </Button>
                      )
                    )}
                    
                    {/* Admin Booking Management Button */}
                    {currentUser?.role === 'Administrator' && onViewEventBookings && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        title="View bookings for this event"
                        onClick={() => onViewEventBookings(event)}
                      >
                        <Users className="h-4 w-4" />
                      </Button>
                    )}
                    
                    {currentUser?.role === 'Administrator' && (
                      <>
                        <CopyEventLinkButton event={event} />
                        <Button size="sm" variant="ghost" title="Edit Event" onClick={() => editEvent(event)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Delete Event" onClick={() => deleteEvent(event.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr>
                <td colSpan={emptyColSpan} className="text-center py-8 text-slate-500">
                  No events found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // Card View
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
      {(Array.isArray(events) ? events : []).map((event) => (
        <Card key={event.id} className={`hover:shadow-lg transition-shadow cursor-pointer ${getEventCardColor(event)}`} onClick={() => viewEvent && viewEvent(event)}>
          {/* Event Image */}
          <div className="relative h-28 sm:h-32 bg-slate-100 rounded-t-lg overflow-hidden flex items-center justify-center">
            {getEventImageUrl(event) ? (
              <img
                src={getEventImageUrl(event)}
                alt={`${event.title} image`}
                className="max-w-full max-h-32 object-contain rounded"
                onError={(e) => handleEventImageError(e, event)}
              />
            ) : null}
            <div 
              className={`${getEventImageUrl(event) ? 'hidden' : 'flex'} w-full h-full items-center justify-center bg-slate-100 text-slate-400`}
            >
              <Calendar className="h-8 w-8" />
            </div>

            {/* CANCELLED overlay — full-card banner across the image */}
            {event.status === 'cancelled' && (
              <div
                className="absolute inset-0 bg-red-900/70 flex items-center justify-center pointer-events-none"
                data-testid={`event-cancelled-overlay-${event.id}`}
              >
                <span className="px-4 py-1 bg-red-700 text-white text-lg font-bold tracking-widest uppercase rotate-[-8deg] shadow-lg border-2 border-white">
                  Cancelled
                </span>
              </div>
            )}

            {/* Status Badge Overlay */}
            <div className="absolute top-2 right-2 flex gap-1">
              {getStatusBadge(event)}
              {isEventUpcoming(event) && (
                <Badge variant="outline" className="text-xs text-blue-600 bg-white">
                  Upcoming
                </Badge>
              )}
            </div>
          </div>
          
          <CardHeader className="p-3 sm:p-6 pb-2 sm:pb-3">
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base sm:text-lg hover:text-blue-600 leading-tight">{event.title}</CardTitle>
                {event.event_label && (
                  <CardDescription className="flex items-center gap-1 mt-1 text-xs">
                    <Tag className="h-3 w-3" />
                    {event.event_label}
                  </CardDescription>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <div className="space-y-2 sm:space-y-3 text-sm">
              <div>
                <p className="text-slate-600 line-clamp-3">
                  {stripHtmlTags(event.description_text)}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span>{formatDate(event.event_date)}</span>
              </div>

              {event.venue_ids && event.venue_ids.length > 0 && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <span>{event.venue_ids.length} venue{event.venue_ids.length > 1 ? 's' : ''}</span>
                </div>
              )}

              {event.organiser_ids && event.organiser_ids.length > 0 && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" />
                  <span>{event.organiser_ids.length} organiser{event.organiser_ids.length > 1 ? 's' : ''}</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Euro className="h-4 w-4 text-slate-400" />
                <span className="font-medium text-green-600">
                  {formatEventPricing(event)}
                </span>
              </div>

              {/* Guided Visit indicator */}
              {event.guided_visit && (
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-500" />
                  <span className="text-blue-600 text-xs font-medium">Guided Visit Available</span>
                </div>
              )}

                  {event.category_tags && event.category_tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {(Array.isArray(event.category_tags) ? event.category_tags : []).slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {event.category_tags.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{event.category_tags.length - 3} more
                    </Badge>
                  )}
                </div>
              )}

              {/* Low ticket warning on card — admin only */}
              {currentUser?.role === 'Administrator' &&
               eventAvailability[event.id]?.remaining_tickets != null && 
               eventAvailability[event.id].remaining_tickets > 0 && 
               eventAvailability[event.id].remaining_tickets <= 5 && (
                <div className="flex items-center gap-1" data-testid={`low-ticket-card-${event.id}`}>
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Only {eventAvailability[event.id].remaining_tickets} ticket{eventAvailability[event.id].remaining_tickets !== 1 ? 's' : ''} left!
                  </Badge>
                </div>
              )}
              {/* Sold out badge on card */}
              {eventAvailability[event.id]?.is_sold_out && (
                <div data-testid={`sold-out-card-${event.id}`}>
                  <Badge className="bg-red-100 text-red-800 border-red-300 text-xs">
                    Sold Out
                  </Badge>
                </div>
              )}

              <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
                {/* User Booking Button */}
                {currentUser && currentUser.role !== 'Administrator' && isBookingAvailable(event) && onBookEvent && (
                  isLegacyPricing(event) ? (
                    <Badge className="bg-slate-100 text-slate-600 border-slate-300 px-3 py-1 flex items-center" title="Legacy pricing — booking closed" data-testid={`legacy-locked-card-${event.id}`}>
                      <AlertCircle className="h-3 w-3 mr-1" /> Booking closed
                    </Badge>
                  ) : pendingBookingsByEvent[event.id] ? (
                    <Button
                      size="sm"
                      variant="default"
                      className="bg-amber-500 hover:bg-amber-600 text-white flex-1"
                      title="You have a pending checkout. Click to complete payment."
                      onClick={() => { window.location.href = pendingBookingsByEvent[event.id].checkout_url; }}
                      data-testid={`resume-payment-card-${event.id}`}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Resume payment
                    </Button>
                  ) : bookedEventIds.includes(event.id) ? (
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1 flex items-center" data-testid={`booked-badge-list-${event.id}`}>
                      <CheckCircle className="h-3 w-3 mr-1" /> Booked
                    </Badge>
                  ) : eventAvailability[event.id]?.is_sold_out ? (
                    <Button 
                      size="sm" 
                      variant="default"
                      className="bg-orange-600 hover:bg-orange-700 text-white flex-1"
                      onClick={() => onBookEvent(event)}
                      data-testid={`join-waitlist-card-${event.id}`}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Join Waiting List
                    </Button>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="default"
                      className="bg-green-600 hover:bg-green-700 text-white flex-1"
                      onClick={() => onBookEvent(event)}
                      data-testid={`book-event-list-${event.id}`}
                    >
                      <Calendar className="h-4 w-4 mr-1" />
                      Book Event
                    </Button>
                  )
                )}
                
                {/* Admin Booking Management Button */}
                {currentUser?.role === 'Administrator' && onViewEventBookings && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onViewEventBookings(event)}
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  >
                    <Users className="h-4 w-4 mr-1" />
                    View Bookings
                  </Button>
                )}
                
                {currentUser?.role === 'Administrator' && (
                  <div className="flex gap-1">
                    <CopyEventLinkButton event={event} />
                    <Button size="sm" variant="ghost" title="Edit Event" onClick={() => editEvent(event)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" title="Delete Event" onClick={() => deleteEvent(event.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      
      {events.length === 0 && (
        <div className="col-span-full text-center py-12">
          <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No events found</p>
        </div>
      )}
    </div>
  );
};

// Detailed Event View Component
export const EventDetailView = ({ 
  event, 
  organisers = [], 
  venues = [], 
  isOpen, 
  onClose, 
  currentUser, 
  editEvent, 
  deleteEvent,
  onBookEvent,
  bookedEventIds = [],
  pendingBookingsByEvent = {},
  eventAvailability = {},
}) => {
  if (!event) return null;

  // Helper function to get event image URL with S3 support (local to this component)
  const getEventImageUrl = (event) => {
    // Priority 1: Use S3 images via backend proxy if available
    if (event.images_s3 && event.images_s3.length > 0) {
      const filename = event.images_s3[0].split('/').pop(); // Extract filename from S3 key
      return `${process.env.REACT_APP_BACKEND_URL}/api/images/event/${filename}`;
    }
    
    // Priority 2: Use image URLs directly if available
    if (event.image_urls && event.image_urls.length > 0) {
      const imageUrl = event.image_urls[0];
      const filename = imageUrl.split('/').pop(); // Extract filename from URL
      return `${process.env.REACT_APP_BACKEND_URL}/api/images/event/${filename}`;
    }
    
    // Priority 3: Use local images
    if (event.images && event.images.length > 0) {
      const filename = event.images[0];
      return `${process.env.REACT_APP_BACKEND_URL}/api/images/event/${filename}`;
    }
    
    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: '2-digit',
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "Free Event";
    return `€${parseFloat(amount).toFixed(2)}`;
  };

  const formatEventCosts = (fullCost, reducedCost, priceCategories, matrixPricing = null) => {
    // Handle matrix pricing first (Opera-style Galleria/Platea)
    if (matrixPricing) {
      const sectionPrices = [];
      
      // Check for Galleria pricing (use the first available price from any enabled category)
      if (matrixPricing.galleria_enabled) {
        let galleriaPrice = null;
        if (matrixPricing.member_pricing_enabled && matrixPricing.member_pricing?.galleria_price > 0) {
          galleriaPrice = matrixPricing.member_pricing.galleria_price;
        } else if (matrixPricing.senior_pricing_enabled && matrixPricing.senior_pricing?.galleria_price > 0) {
          galleriaPrice = matrixPricing.senior_pricing.galleria_price;
        } else if (matrixPricing.minor_pricing_enabled && matrixPricing.minor_pricing?.galleria_price > 0) {
          galleriaPrice = matrixPricing.minor_pricing.galleria_price;
        } else if (matrixPricing.young_adult_pricing_enabled && matrixPricing.young_adult_pricing?.galleria_price > 0) {
          galleriaPrice = matrixPricing.young_adult_pricing.galleria_price;
        }
        
        if (galleriaPrice) {
          const section1Label = event.section_1_label || "Galleria";
          sectionPrices.push(`€${parseFloat(galleriaPrice).toFixed(2)} (${section1Label})`);
        }
      }
      
      // Check for Platea pricing (use the first available price from any enabled category)
      if (matrixPricing.platea_enabled) {
        let plateaPrice = null;
        if (matrixPricing.member_pricing_enabled && matrixPricing.member_pricing?.platea_price > 0) {
          plateaPrice = matrixPricing.member_pricing.platea_price;
        } else if (matrixPricing.senior_pricing_enabled && matrixPricing.senior_pricing?.platea_price > 0) {
          plateaPrice = matrixPricing.senior_pricing.platea_price;
        } else if (matrixPricing.minor_pricing_enabled && matrixPricing.minor_pricing?.platea_price > 0) {
          plateaPrice = matrixPricing.minor_pricing.platea_price;
        }
        
        if (plateaPrice) {
          const section2Label = event.section_2_label || "Platea";
          sectionPrices.push(`€${parseFloat(plateaPrice).toFixed(2)} (${section2Label})`);
        }
      }
      
      if (sectionPrices.length === 0) return "Free Event";
      if (sectionPrices.length === 1) return sectionPrices[0];
      return sectionPrices.join(' / ');
    }
    
    // Handle standard sophisticated pricing structure
    if (priceCategories) {
      const enabledPrices = [];
      
      if (priceCategories.member_price_enabled && priceCategories.member_price > 0) {
        enabledPrices.push(`€${parseFloat(priceCategories.member_price).toFixed(2)} (Member)`);
      }
      if (priceCategories.minor_price_enabled && priceCategories.minor_price > 0) {
        enabledPrices.push(`€${parseFloat(priceCategories.minor_price).toFixed(2)} (Minor)`);
      }
      if (priceCategories.senior_price_enabled && priceCategories.senior_price > 0) {
        enabledPrices.push(`€${parseFloat(priceCategories.senior_price).toFixed(2)} (Senior)`);
      }
      if (priceCategories.young_adult_price_enabled && priceCategories.young_adult_price > 0) {
        enabledPrices.push(`€${parseFloat(priceCategories.young_adult_price).toFixed(2)} (Young Adult)`);
      }
      
      if (enabledPrices.length === 0) return "Free Event";
      if (enabledPrices.length === 1) return enabledPrices[0];
      if (enabledPrices.length === 2) return enabledPrices.join(' / ');
      return `${enabledPrices[0]} / +${enabledPrices.length - 1} more`;
    }
    
    // Fallback to old pricing structure
    const fullPrice = fullCost && fullCost > 0 ? formatCurrency(fullCost) : null;
    const reducedPrice = reducedCost && reducedCost > 0 ? formatCurrency(reducedCost) : null;
    
    if (!fullPrice && !reducedPrice) return "Free Event";
    if (fullPrice && reducedPrice) return `${fullPrice} / ${reducedPrice} (reduced)`;
    if (fullPrice) return fullPrice;
    if (reducedPrice) return `${reducedPrice} (reduced price)`;
    return "Free Event";
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      draft: "bg-gray-100 text-gray-800",
      published: "bg-green-100 text-green-800", 
      cancelled: "bg-red-100 text-red-800"
    };

    return (
      <Badge className={statusColors[status] || statusColors.draft}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Draft'}
      </Badge>
    );
  };

  const getOrganiserDetails = (organiserIds) => {
    if (!organiserIds || organiserIds.length === 0) return [];
    return organiserIds.map(id => {
      const organiser = (Array.isArray(organisers) ? organisers : []).find(o => o.id === id);
      return organiser || { id, name: `Unknown Organiser (${id})` };
    });
  };

  const getVenueDetails = (venueIds) => {
    if (!venueIds || venueIds.length === 0) return [];
    return venueIds.map(id => {
      const venue = (Array.isArray(venues) ? venues : []).find(v => v.id === id);
      return venue || { id, name: `Unknown Venue (${id})` };
    });
  };

  const isEventUpcoming = (event) => {
    if (!event.event_date) return true; // If no date, assume it's bookable
    const eventDate = new Date(event.event_date);
    return eventDate > new Date();
  };

  // Check if booking is available for an event
  const isBookingAvailable = (event) => {
    if (!isEventUpcoming(event)) return false;
    
    // Check if event has any pricing configured (new sophisticated pricing)
    if (event.price_categories) {
      const hasAnyPrice = (event.price_categories.member_price_enabled && event.price_categories.member_price > 0) ||
                         (event.price_categories.minor_price_enabled && event.price_categories.minor_price > 0) ||
                         (event.price_categories.senior_price_enabled && event.price_categories.senior_price > 0) ||
                         (event.price_categories.young_adult_price_enabled && event.price_categories.young_adult_price > 0);
      return hasAnyPrice;
    }
    
    // Fallback to old pricing structure for backward compatibility
    return (event.cost_euro_full > 0 || event.cost_euro_reduced > 0);
  };

  const eventOrganisers = getOrganiserDetails(event.organiser_ids);
  const eventVenues = getVenueDetails(event.venue_ids);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <DialogTitle className="text-2xl mb-2">{event.title}</DialogTitle>
              
              {/* Event ID - displayed as a small badge for reference */}
              <div className="mb-2">
                <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600">
                  Event ID: {event.id}
                </Badge>
              </div>
              
              <div className="flex gap-2 mb-2">
                {getStatusBadge(event.status)}
                {isEventUpcoming(event) && (
                  <Badge variant="outline" className="text-blue-600">
                    Upcoming Event
                  </Badge>
                )}
                {event.event_label && (
                  <Badge variant="outline">
                    <Tag className="h-3 w-3 mr-1" />
                    {event.event_label}
                  </Badge>
                )}
              </div>
            </div>
            {currentUser?.role === 'Administrator' && (
              <div className="flex gap-2">
                <CopyEventLinkButton event={event} withLabel />
                <Button size="sm" variant="outline" onClick={() => editEvent(event)}>
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => deleteEvent(event.id)}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Member Booking CTA — prominent, sticky at top of the dialog body so
            "Book" is immediately visible when ANY logged-in user (member OR
            administrator) opens any event view. Administrators are members
            too and need to be able to book events for themselves, so the CTA
            no longer excludes them; admin actions (Edit/Delete) live in the
            header and are unaffected. */}
        {currentUser && onBookEvent && isEventUpcoming(event) && (
          <div
            className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 shadow-sm"
            data-testid={`event-detail-book-cta-${event.id}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-emerald-900">
                {bookedEventIds.includes(event.id)
                  ? "You're booked for this event"
                  : pendingBookingsByEvent[event.id]
                    ? 'You have a pending payment — finish it before your seat is released'
                    : eventAvailability[event.id]?.is_sold_out
                      ? 'This event is sold out — join the waiting list to be notified'
                      : 'Reserve your seat now'}
              </p>
              {eventAvailability[event.id]?.remaining_tickets != null &&
               eventAvailability[event.id].remaining_tickets > 0 &&
               eventAvailability[event.id].remaining_tickets <= 5 && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Only {eventAvailability[event.id].remaining_tickets} ticket
                  {eventAvailability[event.id].remaining_tickets !== 1 ? 's' : ''} left!
                </p>
              )}
            </div>
            <div className="shrink-0">
              {isLegacyPricing(event) ? (
                <Badge className="bg-slate-100 text-slate-600 border-slate-300 px-4 py-2 text-sm" title="Legacy pricing — booking closed" data-testid={`legacy-locked-detail-${event.id}`}>
                  <AlertCircle className="h-4 w-4 mr-2" /> Booking closed (legacy pricing)
                </Badge>
              ) : pendingBookingsByEvent[event.id] ? (
                <Button
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 shadow-md w-full sm:w-auto"
                  onClick={() => { window.location.href = pendingBookingsByEvent[event.id].checkout_url; }}
                  data-testid={`resume-payment-detail-${event.id}`}
                >
                  <Clock className="h-5 w-5 mr-2" />
                  Resume payment
                </Button>
              ) : bookedEventIds.includes(event.id) ? (
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-4 py-2 text-sm" data-testid={`booked-badge-detail-${event.id}`}>
                  <CheckCircle className="h-4 w-4 mr-2" /> Booked
                </Badge>
              ) : eventAvailability[event.id]?.is_sold_out ? (
                <Button
                  size="lg"
                  className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 shadow-md w-full sm:w-auto"
                  onClick={() => { onBookEvent(event); onClose(); }}
                  data-testid={`join-waitlist-detail-${event.id}`}
                >
                  <Clock className="h-5 w-5 mr-2" />
                  Join Waiting List
                </Button>
              ) : (
                <Button
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 shadow-md w-full sm:w-auto"
                  onClick={() => { onBookEvent(event); onClose(); }}
                  data-testid={`book-event-detail-${event.id}`}
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  Book this event
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Event Image - displayed between title and description */}
        {((event.images && event.images.length > 0) || (event.images_s3 && event.images_s3.length > 0)) && (
          <div className="mt-4 flex justify-center">
            <img 
              src={getEventImageUrl(event)}
              alt={event.title}
              className="max-w-full max-h-64 object-contain rounded-lg shadow-md"
              onError={(e) => {
                console.warn(`Event detail image missing for event: ${event.title} (ID: ${event.id})`);
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
          {/* Main Event Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Event Description</CardTitle>
              </CardHeader>
              <CardContent>
                {event.description_html ? (
                  <div 
                    className="prose prose-sm max-w-none prose-headings:text-slate-900 prose-a:text-blue-600 prose-ul:list-disc prose-ol:list-decimal"
                    /* Sanitize admin-authored HTML before rendering to defuse any future content-injection path. */
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(event.description_html, {
                      ALLOWED_TAGS: ['a','b','i','em','strong','u','p','br','ul','ol','li','span','div','h1','h2','h3','h4','h5','h6','blockquote','pre','code','img','hr','table','thead','tbody','tr','th','td'],
                      ALLOWED_ATTR: ['href','target','rel','class','style','src','alt','title']
                    }) }}
                  />
                ) : (
                  <p className="text-slate-700 whitespace-pre-wrap">{event.description_text}</p>
                )}
              </CardContent>
            </Card>

            {/* Organisers */}
            {eventOrganisers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Organisers ({eventOrganisers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(Array.isArray(eventOrganisers) ? eventOrganisers : []).map((organiser) => (
                      <div key={organiser.id} className="border rounded-lg p-3">
                        <h4 className="font-medium">{organiser.name}</h4>
                        {organiser.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                            <Mail className="h-4 w-4" />
                            <a href={`mailto:${organiser.email}`} className="hover:text-blue-600">
                              {organiser.email}
                            </a>
                          </div>
                        )}
                        {organiser.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                            <Phone className="h-4 w-4" />
                            <a href={`tel:${organiser.phone}`} className="hover:text-blue-600">
                              {organiser.phone}
                            </a>
                          </div>
                        )}
                        {organiser.website && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                            <Globe className="h-4 w-4" />
                            <a href={organiser.website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                              {organiser.website}
                            </a>
                          </div>
                        )}
                        {organiser.bio && (
                          <p className="text-sm text-slate-600 mt-2">{organiser.bio}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Venues */}
            {eventVenues.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Venues ({eventVenues.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(Array.isArray(eventVenues) ? eventVenues : []).map((venue) => (
                      <div key={venue.id} className="border rounded-lg p-3">
                        <h4 className="font-medium">{venue.name}</h4>
                        {venue.address && (
                          <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                            <Building2 className="h-4 w-4" />
                            <span>{venue.address}</span>
                          </div>
                        )}
                        {venue.city && (
                          <p className="text-sm text-slate-600 mt-1">{venue.city}</p>
                        )}
                        {venue.capacity && (
                          <p className="text-sm text-slate-600 mt-1">
                            Capacity: {venue.capacity} people
                          </p>
                        )}
                        {venue.facilities && venue.facilities.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {(Array.isArray(venue.facilities) ? venue.facilities : []).map((facility, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {facility}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {venue.contact_info && (
                          <p className="text-sm text-slate-600 mt-2">{venue.contact_info}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Event Info Sidebar */}
          <div className="space-y-4">
            {/* Date & Time */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date & Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{formatDate(event.event_date)}</p>
                {event.booking_deadline && (
                  <p className="text-xs text-amber-700 mt-2" data-testid="event-detail-booking-deadline">
                    <span className="font-semibold">Booking closes:</span> {formatDate(event.booking_deadline)}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Cost */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Euro className="h-4 w-4" />
                  Cost
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold text-green-600">
                  {formatEventPricing(event)}
                </p>
              </CardContent>
            </Card>

            {/* Categories */}
            {event.category_tags && event.category_tags.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(event.category_tags) ? event.category_tags : []).map((tag, index) => (
                      <Badge key={index} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Event Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Event Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Status:</span> {getStatusBadge(event.status)}
                </div>
                <div>
                  <span className="font-medium">Guided Visit:</span> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    event.guided_visit 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {event.guided_visit ? 'Available' : 'Not Available'}
                  </span>
                </div>
                {event.created_at && (
                  <div>
                    <span className="font-medium">Created:</span> {new Date(event.created_at).toLocaleDateString('en-GB')}
                  </div>
                )}
                {event.updated_at && event.updated_at !== event.created_at && (
                  <div>
                    <span className="font-medium">Updated:</span> {new Date(event.updated_at).toLocaleDateString('en-GB')}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventsTabContent;